import * as yup from "yup";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoDocumentOutline, IoSaveOutline } from "react-icons/io5";
import { getBase64 } from "../../../utils/stringUtils";
import { Document, Page, pdfjs } from "react-pdf";
import { OR_ALREADY_EXISTS } from "../../../utils/constants";

import {
  MULTIMEDIA_MIME_TYPES,
  MULTIMEDIA_PLACEHOLDER,
} from "../../../utils/constants";

import InnerImageZoom from "react-inner-image-zoom";
import Textbox from "../../../components/common/Textbox";
import TextboxAppend from "../../../components/common/TextboxAppend";
import Datepicker from "../../../components/common/Datepicker";
import ReactSelect from "../../../components/common/ReactSelect";
import FileUpload from "../../../components/common/FileUpload";
import Toaster from "./../../../components/common/Toaster";

import taxService from "./../../../services/taxService";
import moment from "moment";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const schema = yup.object({
  number: yup.string().required("Please enter OR No."),
  gross_amount: yup.string().required("Please enter gross amount."),
  discount_amount: yup.string().required("Please enter discount amount."),
  withheld_amount: yup.string().required("Please enter withheld amount."),
  tax_rate: yup.string().required("Please enter tax rate."),
  client_name: yup.string().required("Please enter client name.").nullable(),
  date_issued: yup.string().required("Please select date issued."),
  type: yup.object().shape({
    label: yup.string().required("Please select client type."),
    value: yup.string().required("Please select client type."),
  }),
  quarter: yup.object().shape({
    label: yup.string().required("Please select quarter."),
    value: yup.string().required("Please select quarter."),
  }),
  file: yup.mixed().when("id", {
    is: (value) => !value,
    then: yup.mixed().required("Please select image."),
    otherwise: yup.mixed(),
  }),
});

const ReceiptForm = (props) => {
  const { id, data, isLoading, setLoading, setShowForm, onSuccess } = props;

  const [orExists, setOrExists] = useState(false);
  const [showSeniorForm, setShowSeniorForm] = useState(false);

  const [grossAmount, setGrossAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [withheldAmount, setWithheldAmount] = useState(0);
  const [netReceipt, setNetReceipt] = useState(0);
  const [netAmount, setNetAmount] = useState(0);
  const [taxRate, setTaxRate] = useState(0);

  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);

  const [dateIssued, setDateIssued] = useState(
    Date.parse(moment(new Date(), "MM-DD-yyyy").toISOString())
  );

  const [seniorDateIssued, setSeniorDateIssued] = useState(null);
  const [seniorExpiryDate, setSeniorExpiryDate] = useState(null);

  const quarters = taxService.getAltTaxQuarters();
  const types = taxService.getClientTypes();

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const handleGrossChange = (e) => {
    setGrossAmount(e.target.value);
    handleCalculate(e.target.value, discountAmount, withheldAmount);
  };

  const handleDiscountChange = (e) => {
    setDiscountAmount(e.target.value);
    handleCalculate(grossAmount, e.target.value, withheldAmount);
  };

  const handleWithheldChange = (e) => {
    setWithheldAmount(e.target.value);
    handleCalculate(grossAmount, discountAmount, e.target.value);
  };

  const handleCalculate = (gross, discount, withheld) => {
    const net_receipt = (gross - discount).toFixed(2);
    const net_amount = (net_receipt - withheld).toFixed(2);
    const tax_rate = Math.round((withheld / net_receipt).toFixed(2) * 100);

    setValue("net_receipt", net_receipt);
    setValue("net_amount", net_amount);
    setValue("tax_rate", tax_rate);

    setNetReceipt(net_receipt);
    setNetAmount(net_amount);
    setTaxRate(tax_rate);
  };

  const handleChangeClientType = (e) => {
    const selected = e.target.value;

    if (selected.value >= 3) {
      setShowSeniorForm(true);
      return;
    }

    setShowSeniorForm(false);
  };

  const onSubmit = async (info) => {
    try {
      setLoading(true);

      const item = { ...info };
      item["account_id"] = id;
      item["quarter"] = info.quarter.value;
      item["quarter_name"] = info.quarter.label;
      item["type"] = info.type.value;
      item["client_type"] = info.type.label;

      let document_name;

      if (!data) {
        const base = await getBase64(item.file[0]);
        const content = base.split(",")[1];
        const extension = item.file[0].path.split(".").pop();
        const file_name = `${moment().format("YYYYMMDDmss")}${id}.${extension}`;
        const file = { file_name, content };

        document_name = file_name;

        item["file"] = file;
      }

      const result = await taxService.saveAccountReceiptAsync(item);

      item["id"] = result.id;

      item["gross_value"] = info.gross_amount;
      item["discount_value"] = info.discount_amount;
      item["withheld_value"] = info.withheld_amount;
      item["net_value"] = info.net_amount;

      item["gross_amount"] = Number(info.gross_amount).toLocaleString();
      item["withheld_amount"] = Number(info.withheld_amount).toLocaleString();
      item["net_amount"] = Number(info.net_amount).toLocaleString();

      item["document_name"] = data ? data.document_name : document_name;
      item["created_on"] = data
        ? moment(data.created_on).format("LL")
        : moment().format("LL");

      item["s3_reference_name"] = data
        ? data.s3_reference_name
        : result.s3_reference_name;

      Toaster.Success("OR Saved Successfully!");
      onSuccess(item);
      setShowForm(false);
      setLoading(false);
    } catch (ex) {
      if (ex.response && ex.response.data.message === OR_ALREADY_EXISTS) {
        setOrExists(true);
        setLoading(false);
        return;
      }

      setLoading(false);
      Toaster.Error("Something went wrong.");
    }
  };

  useEffect(() => {
    if (data) {
      setValue("id", data.id);
      setValue("number", data.number);
      setValue("tax_rate", data.tax_rate);
      setValue("gross_amount", data.gross_value);
      setValue("discount_amount", data.discount_value);
      setValue("net_receipt", data.net_receipt);
      setValue("withheld_amount", data.withheld_value);
      setValue("net_amount", data.net_value);
      setValue("client_name", data.client_name);

      setTaxRate(data.tax_rate);
      setGrossAmount(data.gross_value);
      setDiscountAmount(data.discount_value);
      setNetReceipt(data.net_receipt);
      setWithheldAmount(data.withheld_value);
      setNetAmount(data.net_value);

      if (data.client_name) {
        const date_issued = moment(data.date_issued).format("MM-DD-yyyy");

        const selected_type = {
          value: data.type,
          label: data.client_type,
        };

        const selected_quarter = {
          value: data.quarter,
          label: data.quarter_name,
        };

        setValue("type", selected_type);
        setValue("quarter", selected_quarter);
        setValue("date_issued", date_issued);
        setDateIssued(date_issued);
      }

      if (data.type >= 3) {
        setShowSeniorForm(true);

        setValue("senior_number", data.senior_number);

        if (data.senior_date_issued) {
          const senior_date_issued = moment(data.senior_date_issued).format(
            "MM-DD-yyyy"
          );

          setValue("senior_date_issued", senior_date_issued);
          setSeniorDateIssued(senior_date_issued);
        }

        if (data.senior_expiry_date) {
          const senior_expiry_date = moment(data.senior_expiry_date).format(
            "MM-DD-yyyy"
          );

          setValue("senior_expiry_date", senior_expiry_date);
          setSeniorExpiryDate(senior_expiry_date);
        }
      }
    }
  }, [data, setValue]);

  const onDocumentLoad = ({ numPages }) => {
    setNumPages(numPages);
  };

  const renderFile = (url) => {
    const extension = url.split(".").pop().toLowerCase();

    if (extension === "pdf") {
      return (
        <>
          <Document file={{ url }} onLoadSuccess={onDocumentLoad}>
            <Page pageNumber={page} width={500} />
          </Document>
          <div className="flex justify-center text-center mb-3 text-gray-500">
            <button
              className="px-4 py-1 border rounded-lg"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              type="button"
              aria-label="Previous"
            >
              ‹
            </button>
            <span className="px-3 mt-2 text-sm">
              {page} / {numPages}
            </span>
            <button
              className="px-4 py-1 border rounded-lg"
              disabled={page >= numPages}
              onClick={() => setPage(page + 1)}
              type="button"
              aria-label="Next"
            >
              ›
            </button>
            <a
              className="button border w-12 ml-2 hover:border-blue-400"
              href={url}
              target="_blank"
              rel="noreferrer"
              title="Download File"
              download
            >
              <IoDocumentOutline className="text-lg" />
            </a>
          </div>
        </>
      );
    }

    return <InnerImageZoom src={url} zoomSrc={url} hideHint={true} />;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2 gap-16">
        {data && (
          <div className="col-span-1">
            <div className="border">{renderFile(data.s3_reference_name)}</div>
          </div>
        )}
        <div className={`space-y-6 ${!data ? "col-span-2" : "col-span-1"}`}>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              {!data && (
                <FileUpload
                  id="file-dropdown"
                  name="file"
                  title="Document"
                  setValue={setValue}
                  clearErrors={clearErrors}
                  register={register("file")}
                  accepts={MULTIMEDIA_MIME_TYPES}
                  placeholder={MULTIMEDIA_PLACEHOLDER}
                  maxFiles={1}
                  maxSize={5242880}
                  multiple={false}
                  errors={errors}
                />
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-1">
              <Textbox
                id="gross-textbox"
                name="gross_amount"
                type="number"
                title="Gross Amount"
                placeholder="0.00"
                step=".01"
                register={register("gross_amount", {
                  onChange: (e) => handleGrossChange(e),
                })}
                errors={errors}
              />
            </div>
            <div className="col-span-1">
              <Textbox
                id="discount-textbox"
                name="discount_amount"
                type="number"
                title="Discount"
                placeholder="0.00"
                step=".01"
                register={register("discount_amount", {
                  onChange: (e) => handleDiscountChange(e),
                })}
                errors={errors}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-1">
              <Textbox
                id="withheld-textbox"
                name="withheld_amount"
                placeholder="0.00"
                type="text"
                title="Withheld Amount"
                register={register("withheld_amount", {
                  onChange: (e) => handleWithheldChange(e),
                })}
                errors={errors}
              />
            </div>
            <div className="col-span-1">
              <Textbox
                id="net-textbox"
                name="net_receipt"
                type="text"
                title="Net Receipts"
                value={netReceipt}
                register={register("net_receipt")}
                disabled
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-1">
              <Textbox
                id="net-textbox"
                name="net_amount"
                type="text"
                title="Net Amount"
                value={netAmount}
                register={register("net_amount")}
                disabled
              />
            </div>
            <div className="col-span-1">
              <TextboxAppend
                id="tax-rate-textbox"
                title="Tax Rate"
                type="number"
                name="tax_rate"
                value={taxRate}
                append={<span className="text-gray-400">%</span>}
                errors={errors}
                disabled
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <Textbox
                id="number-textbox"
                name="number"
                type="text"
                title="OR No."
                register={register("number")}
                errors={errors}
              />
              {orExists && (
                <div className="pt-2">
                  <span className="text-sm text-red-400">
                    {OR_ALREADY_EXISTS}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-1">
              <Datepicker
                id="date-issued"
                name="date_issued"
                title="Date Issued"
                dateFormat="MM-DD-yyyy"
                timeFormat={false}
                setValue={setValue}
                clearErrors={clearErrors}
                register={register("date_issued")}
                onDateChange={setDateIssued}
                value={dateIssued}
                initialValue={dateIssued}
                errors={errors}
              />
            </div>
            <div className="col-span-1">
              <ReactSelect
                id="quarter-dropdown"
                name="quarter"
                title="Quarter"
                options={quarters}
                control={control}
                register={register("quarter")}
                errors={errors}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <Textbox
                id="client-name-textbox"
                name="client_name"
                type="text"
                title="Client Name"
                register={register("client_name")}
                errors={errors}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <ReactSelect
                id="type-dropdown"
                name="type"
                title="Client Type"
                options={types}
                control={control}
                register={register("type", {
                  onChange: (e) => handleChangeClientType(e),
                })}
                errors={errors}
              />
            </div>
          </div>
          {showSeniorForm && (
            <>
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <Textbox
                    id="senior-number-textbox"
                    name="senior_number"
                    type="text"
                    title="ID No."
                    register={register("senior_number")}
                    errors={errors}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-1">
                  <Datepicker
                    id="senior-date-issued"
                    name="senior_date_issued"
                    title="ID Issued Date"
                    dateFormat="MM-DD-yyyy"
                    timeFormat={false}
                    setValue={setValue}
                    clearErrors={clearErrors}
                    register={register("senior_date_issued")}
                    onDateChange={setSeniorDateIssued}
                    value={seniorDateIssued}
                    initialValue={seniorDateIssued}
                    errors={errors}
                  />
                </div>
                <div className="col-span-1">
                  <Datepicker
                    id="senior-expiry-date"
                    name="senior_expiry_date"
                    title="ID Expiry Date"
                    dateFormat="MM-DD-yyyy"
                    timeFormat={false}
                    setValue={setValue}
                    clearErrors={clearErrors}
                    register={register("senior_expiry_date")}
                    onDateChange={setSeniorExpiryDate}
                    value={seniorExpiryDate}
                    initialValue={seniorExpiryDate}
                    errors={errors}
                  />
                </div>
              </div>
            </>
          )}
          <div className="grid grid-2 pt-2">
            <div className="col-span-2">
              <button
                type="submit"
                className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
                {!isLoading && <IoSaveOutline className="mr-3" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ReceiptForm;
